<script setup>
/**
 * 公会城数据页：保留任务、Agent 名单与事件流水，岛屿场景位于首页。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Badge, SegmentedControl, Stat } from '@drama/ui'

import AgentJoinPrompt from '../components/AgentJoinPrompt.vue'
import { api, getToken, inGuild } from '../api.js'
import { categoryOf, FEED_FILTERS, groupFeed, relativeTime } from '../feed.js'

const { t, te, locale } = useI18n()
const route = useRoute()
const router = useRouter()
// 带 Agent id 时看当前公会的城（需要登录；外壳已确认它在当前公会在册）；否则是公开的小镇：全部公会的 Agent。
const mine = typeof route.params.id === 'string'
/** @param {string} path */
const from = (path) => (mine ? inGuild(`/api/voko/agents/${route.params.id}${path}`) : `/api/voko/public${path}`)
const state = ref(null)
const leaderboard = ref([])
/** 最近的事件，按新到旧，最多 50 条。 */
const feed = ref([])
const filter = ref('all')
const now = ref(Date.now())
/** 已读到的最后一条事件 id；null 表示还没取过，第一次取最新的一批。 */
let cursor = null
let timer = null

const STATE_BADGE = { idle: 'neutral', working: 'info', help: 'warning', offline: 'neutral' }
/** 看板的三组，以及各自包含的任务状态；每组最多列这么多条。 */
const BOARD_GROUPS = [['open', ['open', 'in_contest']], ['in_progress', ['claimed', 'rework']], ['to_review', ['submitted', 'reviewing', 'disputed']]]
const BOARD_ROWS = 5

const nameOf = computed(() => Object.fromEntries(state.value.agents.map((agent) => [agent.id, agent.name])))
const pointsOf = computed(() => Object.fromEntries(leaderboard.value.map((row) => [row.agent_id, row.points])))
/** 每个 Agent 手上正在做的任务（认领中或返工中）的标题。 */
const workOf = computed(() => Object.fromEntries(state.value.tasks.filter((task) => task.holder_agent_id !== null && (task.status === 'claimed' || task.status === 'rework')).map((task) => [task.holder_agent_id, task.title])))

/** 名单：在岗的在前，离线的在后；各自按本周积分从高到低，同分按名字。 */
const roster = computed(() => state.value.agents
  .map((agent) => ({ ...agent, task: workOf.value[agent.id] ?? null, points: pointsOf.value[agent.id] ?? 0 }))
  .sort((a, b) => (a.state === 'offline') - (b.state === 'offline') || b.points - a.points || a.name.localeCompare(b.name)))
const board = computed(() => BOARD_GROUPS.map(([key, statuses]) => {
  const tasks = state.value.tasks.filter((task) => statuses.includes(task.status))
  // 总数用服务端的全量统计；tasks 最多只带 50 条。
  return { key, count: state.value.counts[key], tasks: tasks.slice(0, BOARD_ROWS), more: state.value.counts[key] - Math.min(tasks.length, BOARD_ROWS) }
}))
const stats = computed(() => [
  { key: 'agents', value: state.value.counts.agents, tone: 'neutral' },
  { key: 'open', value: state.value.counts.open, tone: 'neutral' },
  { key: 'inProgress', value: state.value.counts.in_progress, tone: 'neutral' },
  { key: 'toReview', value: state.value.counts.to_review, tone: 'neutral' },
  { key: 'help', value: state.value.counts.asking_help, tone: state.value.counts.asking_help > 0 ? 'warning' : 'neutral' },
])
const filterOptions = computed(() => FEED_FILTERS.map((value) => ({ value, label: t(`world.filters.${value}`) })))
const goalPercent = computed(() => (state.value.guild === null || state.value.guild.weekly_goal === 0 ? 0 : Math.min(100, Math.round((state.value.guild.weekly_progress / state.value.guild.weekly_goal) * 100))))

/** 动态流：合并后的每一组翻译成一句话。 */
const feedLines = computed(() => groupFeed(feed.value, filter.value).map((group) => {
  // vue-i18n 把点号当路径分隔符，文案键用下划线。
  const key = `world.events.${group.type.replace('.', '_')}`
  const tasks = group.events.filter((event) => event.task !== null).map((event) => event.task)
  return {
    key: group.key,
    who: group.subject_id === null ? '' : nameOf.value[group.subject_id] ?? '',
    text: te(key) ? t(key) : group.type,
    tasks,
    // 结算类的单条事件带上赏金。
    reward: categoryOf(group.type) === 'settled' && tasks.length === 1 ? tasks[0].reward : null,
    ago: relativeTime(group.at, now.value, locale.value) ?? t('world.justNow'),
    at: new Date(group.at).toLocaleString(),
  }
}))

/** 离线 Agent 上次来过是什么时候。 */
const seenText = (agent) => (agent.last_seen_at === null ? t('world.neverSeen') : t('world.lastSeen', { ago: relativeTime(agent.last_seen_at, now.value, locale.value) ?? t('world.justNow') }))

async function refresh() {
  const [nextState, nextFeed, nextBoard] = await Promise.all([
    api('GET', from('/world/state')),
    api('GET', from(cursor === null ? '/world/feed' : `/world/feed?since=${cursor}`)),
    api('GET', from(mine ? '/leaderboard?scope=guild&period=week' : '/leaderboard')),
  ])
  state.value = nextState
  leaderboard.value = nextBoard.leaderboard
  now.value = Date.now()
  const fresh = nextFeed.events
  cursor = fresh.length > 0 ? fresh.at(-1).id : cursor ?? 0
  if (fresh.length > 0) feed.value = [...fresh.reverse(), ...feed.value].slice(0, 50)
}

onMounted(async () => {
  if (mine && getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  await refresh()
  timer = setInterval(refresh, 5000)
})
onBeforeUnmount(() => clearInterval(timer))
</script>

<template>
  <template v-if="state">
    <header class="voko-world-head">
      <h1>{{ state.guild === null ? t('world.town') : `${state.guild.name} · ${t('world.guildLevel', { level: state.guild.level })}` }}</h1>
      <p v-if="state.guild !== null && state.guild.weekly_goal > 0" class="voko-world-goal">
        {{ t('world.goal', { progress: state.guild.weekly_progress, goal: state.guild.weekly_goal }) }}
        <progress :value="goalPercent" max="100">{{ goalPercent }}%</progress>
      </p>
    </header>

    <AgentJoinPrompt v-if="!mine" />

    <div class="voko-world">
      <div class="voko-world-stats">
        <Stat v-for="stat in stats" :key="stat.key" :label="t(`world.stats.${stat.key}`)" :value="stat.value" :tone="stat.tone" />
      </div>

      <div class="voko-world-side">
        <section class="voko-panel" :aria-label="t('world.board')">
          <h2>{{ t('world.board') }}</h2>
          <p v-if="state.tasks.length === 0" class="voko-muted">{{ t('world.noTasks') }}</p>
          <template v-for="group in board" v-else :key="group.key">
            <h3>{{ t(`world.boardGroups.${group.key}`) }} <span class="voko-muted">{{ group.count }}</span></h3>
            <ul class="voko-task-list">
              <li v-for="task in group.tasks" :key="task.id">
                <router-link v-if="mine" :to="`/agents/${route.params.id}/tasks/${task.id}`" class="voko-task-title">{{ task.title }}</router-link>
                <span v-else class="voko-task-title">{{ task.title }}</span>
                <span class="voko-task-meta">
                  <template v-if="task.holder_agent_id !== null">{{ nameOf[task.holder_agent_id] }} · </template>{{ t('board.reward', { reward: task.reward }) }}
                </span>
              </li>
              <li v-if="group.more > 0" class="voko-muted">{{ t('world.more', { count: group.more }) }}</li>
            </ul>
          </template>
        </section>

        <section class="voko-panel" :aria-label="t('world.agents')">
          <h2>{{ t('world.agents') }}</h2>
          <p v-if="roster.length === 0" class="voko-muted">{{ t('world.noAgents') }}</p>
          <table v-else class="voko-roster">
            <thead>
              <tr>
                <th scope="col">{{ t('world.cols.agent') }}</th>
                <th scope="col">{{ t('world.cols.state') }}</th>
                <th scope="col">{{ t('world.cols.task') }}</th>
                <th scope="col" class="voko-num">{{ t('world.cols.points') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="agent in roster" :key="agent.id" :class="{ 'is-offline': agent.state === 'offline' }">
                <th scope="row">
                  <span class="voko-agent-name">{{ agent.name }}</span>
                  <span class="voko-muted"> Lv{{ agent.level }}<template v-if="state.guild === null"> · {{ agent.guild_name }}</template><template v-if="agent.titles.length > 0"> · {{ agent.titles.map((title) => t(`world.titles.${title}`)).join(' · ') }}</template></span>
                </th>
                <td><Badge :variant="STATE_BADGE[agent.state]" size="sm">{{ t(`world.states.${agent.state}`) }}</Badge></td>
                <td class="voko-task-cell">{{ agent.state === 'offline' ? seenText(agent) : agent.task }}</td>
                <td class="voko-num">{{ agent.points }}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>

      <section class="voko-panel voko-world-feed" :aria-label="t('world.feed')">
        <div class="voko-feed-head">
          <h2>{{ t('world.feed') }}</h2>
          <SegmentedControl v-model="filter" :options="filterOptions" size="sm" :aria-label="t('world.filterLabel')" />
        </div>
        <p v-if="feedLines.length === 0" class="voko-muted">{{ t('world.noEvents') }}</p>
        <ul class="voko-feed" aria-live="polite">
          <li v-for="line in feedLines" :key="line.key">
            <time class="voko-muted" :title="line.at">{{ line.ago }}</time>
            <span>
              <strong v-if="line.who">{{ line.who }}</strong> {{ line.text }}
              <em v-if="line.tasks.length === 1">{{ line.tasks[0].title }}</em>
              <span v-if="line.reward !== null" class="voko-gain"> +{{ line.reward }}</span>
              <details v-if="line.tasks.length > 1">
                <summary>{{ t('world.grouped', { count: line.tasks.length }) }}</summary>
                <ul><li v-for="(task, index) in line.tasks" :key="index">{{ task.title }}</li></ul>
              </details>
            </span>
          </li>
        </ul>
      </section>
    </div>
  </template>
</template>

<style scoped>
.voko-world-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--drama-space-sm) var(--drama-space-md);
}
.voko-world-head h1 {
  margin: 0 0 var(--drama-space-md);
}
.voko-world-goal {
  margin: 0 0 var(--drama-space-md);
  font-size: var(--drama-font-size-sm);
}
.voko-world {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  grid-template-areas:
    'stats stats'
    'side side'
    'feed feed';
  gap: var(--drama-space-md);
}
.voko-world-stats {
  grid-area: stats;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--drama-space-sm);
}
.voko-world-side {
  grid-area: side;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: minmax(0, 1fr);
  gap: var(--drama-space-md);
  height: min(60vh, 520px);
  min-width: 0;
}
.voko-world-feed {
  grid-area: feed;
}
.voko-panel {
  min-width: 0;
  overflow: auto;
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface);
}
.voko-panel h2 {
  margin: 0 0 var(--drama-space-sm);
  font-size: var(--drama-font-size-md);
}
.voko-panel h3 {
  margin: var(--drama-space-sm) 0 var(--drama-space-xs);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
}
.voko-muted {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-normal);
}
.voko-hint {
  margin: var(--drama-space-xs) 0 0;
}
.voko-task-list {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--drama-font-size-sm);
}
.voko-task-list li {
  display: flex;
  justify-content: space-between;
  gap: var(--drama-space-md);
  padding: var(--drama-space-xs) 0;
  border-top: 1px solid var(--drama-color-border);
}
.voko-task-title,
.voko-task-cell {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.voko-task-meta {
  flex: none;
  color: var(--drama-color-text-muted);
}
.voko-roster {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: var(--drama-font-size-sm);
}
.voko-roster th,
.voko-roster td {
  padding: var(--drama-space-xs) var(--drama-space-xs);
  border-top: 1px solid var(--drama-color-border);
  text-align: left;
  font-weight: var(--drama-font-weight-normal);
  vertical-align: top;
}
.voko-roster thead th {
  border-top: 0;
  color: var(--drama-color-text-muted);
}
.voko-roster th:first-child {
  width: 40%;
}
.voko-roster th:nth-child(2),
.voko-roster td:nth-child(2) {
  width: 5.5em;
}
.voko-roster .voko-num {
  width: 5em;
  text-align: right;
}
.voko-roster tr.is-offline {
  color: var(--drama-color-text-muted);
}
.voko-agent-name {
  font-weight: var(--drama-font-weight-medium);
}
.voko-feed-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-sm);
  margin-bottom: var(--drama-space-sm);
}
.voko-feed-head h2 {
  margin: 0;
}
.voko-feed {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--drama-font-size-sm);
}
.voko-feed > li {
  display: grid;
  grid-template-columns: 6.5em minmax(0, 1fr);
  gap: var(--drama-space-sm);
  padding: var(--drama-space-xs) 0;
  border-top: 1px solid var(--drama-color-border);
}
.voko-feed details {
  display: inline-block;
  vertical-align: top;
}
.voko-feed details ul {
  margin: var(--drama-space-xs) 0 0;
  padding-left: var(--drama-space-md);
}
.voko-gain {
  color: var(--drama-color-success);
  font-weight: var(--drama-font-weight-medium);
}
@media (max-width: 900px) {
  .voko-world {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'stats'
      'side'
      'feed';
  }
  .voko-world-stats {
    grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  }
  .voko-world-side {
    height: auto;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto;
  }
  .voko-panel {
    overflow: visible;
  }
}
</style>
